import axios from 'axios'
import VueRouter from 'vue-router'
import store from '../store'

// Bootstrap instance axios and set defaults
const httpClient = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL, // Base url
  timeout: 600000, // Timout 150 seconds
  headers: {
    'Content-Type': 'application/json'
  }
})

/* ############################################################################
#                                Data endpoints                               #
############################################################################ */

const session = {
  /* #############################################
    #                  Session service           #
    ############################################ */

  async userLogin (credentials) {
    const response = await httpClient.post('/auth/token', credentials)
    return response.data
  },

  async getMe () {
    const response = await httpClient.get('/users/me')
    return response.data
  },

  async resetPassword (data) {
    const response = await httpClient.post('/users/reset_password', data)
    return response.data
  },

  async logout () {
    localStorage.clear()
  }
}

const uploads = {
  /* #############################################
#                    Uploads                   #
############################################ */
  // Upload donations
  async upload (resource, data) {
    const response = await httpClient.post('/upload/' + resource, data)
    return response.data
  }
}

const users = {
  /* ######################################
#                 Users                 #
###################################### */
  async getUsers (filter) {
    const response = await httpClient.get('/users', {
      params: filter
    })
    return response.data
  },

  async getUser (userId) {
    const response = await httpClient.get(`/users/${userId}`)
    return response.data
  },

  // Update user
  async updateUser (user, data) {
    const response = await httpClient.put(`/users/${user}`, data)
    return response.data
  },

  // Delete user
  async deleteUser (user) {
    const response = await httpClient.delete(`/users/${user.id}`)
    return response.data
  }
}

const common = {
  /* #############################################
#                 GET Entities                 #
############################################ */

  // GET * resource:  npos | donations | users | merchants | clients
  async fetchData (resource, data) {
    console.log('DATA', data)
    const response = await httpClient.get(`/${resource}`, {
      params: data
    })
    return response.data
  },

  async fetchLeads (resource, data) {
    const response = await httpClient.get('/export/' + resource, {
      params: data
    })
    return response.data
  }
}

const campaigns = {
  /* #############################################
  #                   Campaigns                  #
  ############################################# */

  // Add Campaign
  async newCampaign (data) {
    const response = await httpClient.post('/campaigns', data)
    return response.data
  },

  // Edit Campaign
  async updateConfiguration (campaign_id, data) {
    const response = await httpClient.put(`/campaigns/${campaign_id}/template`, data)
    return response.data
  },

  // Edit Campaign configuration
  async updateCampaign (data) {
    const response = await httpClient.put(`/campaigns/${data.id}`, data)
    return response.data
  },

  // Delete Campaign
  async deleteCampaign (id) {
    const response = await httpClient.delete(`/campaigns/${id}`)
    return response.data
  },

  // Get specific Campaign
  async getCampaign (id) {
    const response = await httpClient.get(`/campaigns/${id}`)
    return response
  },

  // getCampaignNpos
  // Get Npos associated with campaign
  async getCampaignNpos (campaignId, filter) {
    const response = await httpClient.get(`/npos/get_by/campaign/${campaignId}`, {
      params: filter
    })
    return response.data
  },

  async getCampaignMerchants (campaignId, filter) {
    const response = await httpClient.get(`/merchants/get_by/campaign/${campaignId}`, {
      params: filter
    })
    return response.data
  },

  async getEntityAreas (entityType, entityId) {
    const response = await httpClient.get(`/areas/get_by/${entityType}/${entityId}`)
    return response.data
  },

  async associateArea (entityType, entityId, area_id) {
    const response = await httpClient.post(`/areas/${area_id}/associate/${entityType}/${entityId}`)
    return response.data
  },

  async disassociateArea (entityType, entityId, area_id) {
    const response = await httpClient.delete(`/areas/${area_id}/dissociate/${entityType}/${entityId}`)
    return response.data
  },

  // Get available campaigns for Npo association
  async getAvailableCampaigns (npoId) {
    const response = await httpClient.get(`/campaigns/available/npo/${npoId}`, { params: { npo_id: npoId } })
    return response.data
  },

  // VOTES
  // getCampaignstotal votes
  async getCampaignsTotalVotes (entityType, entityId) {
    let params = {}

    if (entityId && entityId === '') {
      params = {}
    } else {
      params = {
        params: {
          id: entityId,
          type: entityType
        }
      }
    }
    const response = await httpClient.get('/campaigns/totalvotes', params)
    return response.data
  },

  // getCampaign Votes grouped by intervention area
  async getCampaignGroupedVotes (params) {
    const response = await httpClient.get('/votes/group_by/campaign', {
      params: params
    })
    return response.data
  },

  // getCampaign Votes grouped by intervention area
  async getCampaignVotesByArea (params) {
    const response = await httpClient.get('/votes/group_by/area', {
      params: params
    })
    return response.data
  },

  // getCampaign Votes by day by week
  async getCampaignWeeklyVotes (params) {
    const response = await httpClient.get(`/votes/week`, {
      params: params
    })
    return response.data
  },

  // Return most voted Npos sorted by number of votes in the whole campaign
  async getCampaignNposClassification (params) {
    const response = await httpClient.get(`/npos/most-voted`, {
      params: params
    })
    return response.data
  },

  async uploadCampaignRules (data, campaignId) {
    const response = await httpClient.post(`/campaigns/upload/rules/${campaignId}`, data)
    return response.data
  },

  // getCampaignRules
  async getCampaignRules (campaignId) {
    const response = await httpClient.get(`/campaigns/${campaignId}/download/rules`)
    return response.data
  },

  // Associate campaign to entity
  async associateCampaignToEntity (campaigId, entity, entityId) {
    const response = await httpClient.post(`/campaigns/${campaigId}/associate/${entity}/${entityId}`)
    return response.data
  }
}

const clients = {
  /* #############################################
#                    Clients                   #
############################################ */
  // Add Npo
  async newClient (data) {
    const response = await httpClient.post('/clients', data)
    return response
  },

  // Edit Client
  async updateClient (data) {
    const response = await httpClient.put(`/clients/${data.client.id}`, data.client)
    return response.data
  },

  // Delete Npo
  async deleteClient (clientId) {
    const response = await httpClient.delete(`/clients/${clientId}`)
    return response.data
  },

  // Get specific Client
  async getClient (clientId) {
    const response = await httpClient.get(`/clients/${clientId}`)
    return response
  },

  async getClientAvailableCampaigns (client_id) {
    const response = await httpClient.get(`/campaigns/available/client/${client_id}`)
    return response.data
  },

  // Associate campaign to client
  async associateClientCampaign (data) {
    const response = await httpClient.post(`/campaigns/${data.campaign_id}/associate/client/${data.client_id}`, data)
    return response.data
  },

  async disassociateClientCampaign (data) {
    const response = await httpClient.delete(`/campaigns/${data.campaign_id}/dissociate/client/${data.client_id}`)
    return response.data
  },

  async disassociateMerchantCampaign (campaign_id, merchant_id) {
    const response = await httpClient.delete(`/campaigns/${campaign_id}/dissociate/merchant/${merchant_id}`)
    return response.data
  }

}

const contacts = {
  /* #############################################
#                   Contacts                   #
############################################# */
  async createContact (resource, entityId, data) {
    const response = await httpClient.post(`/${resource}/${entityId}/contacts`, data)
    return response
  },

  // Update contact by id
  async updateContact (data) {
    const response = await httpClient.put(`/contacts/${data.id}`, data)
    return response.data
  },

  async getEntityContacts (entity, entityId) {
    const response = await httpClient.get(`/contacts/get_by/${entity}/${entityId}`)
    return response.data
  }
}

const merchants = {
  /* #############################################
  #                   Merchants                  #
  ############################################# */

  // Add Merchant
  async newMerchant (data) {
    const response = await httpClient.post('/merchants', data)
    return response.data
  },

  // Edit Merchant
  async updateMerchant (data) {
    const response = await httpClient.put(`/merchants/${data.id}`, data)
    return response.data
  },

  // Delete Merchant Campaign
  async deleteMerchantCampaign (params) {
    const response = await httpClient.delete('/delete/merchant-campaign', { params: params })
    return response.data
  },

  // Delete Merchant Campaign
  async deleteMerchant (merchant_id) {
    const response = await httpClient.delete(`/merchants/${merchant_id}`)
    return response.data
  },

  // Get specific Client
  async getMerchant (merchantId) {
    const response = await httpClient.get(`/merchants/${merchantId}`)
    return response.data
  },

  // Add Merchant Campaign
  async newMerchantCampaign (data) {
    const response = await httpClient.post(`/campaigns/${data.campaign_id}/associate/merchant/${data.client_id}`, data)
    return response.data
  },

  // Get specific Client
  async getMerchantCampaigns (merchantId) {
    const response = await httpClient.get('/campaigns/get_by/{entity_type}/{entity_id}', {
      params: { id: merchantId }
    })
    return response
  },
  // / campaigns / available / client / { client_id }
  async getMerchantAvailableCampaigns (merchantId) {
    const response = await httpClient.get(`/campaigns/available/merchant/${merchantId}`)
    return response.data
  },

  // GET Merchants by client_id
  async getMerchantsByClient (clientId) {
    const response = await httpClient.get(`/merchants/get_by/client/${clientId}`)

    return response.data
  }

}

const qrcodes = {
  /* ############################################
#                   QRCodes                  #
############################################ */

  async getQrcodes (filter) {
    const response = await httpClient.get(`/qrcode`, { params: filter })
    return response
  },

  // Edit Merchant
  async requestQrcodes (data) {
    const response = await httpClient.post('/qrcode', null, { params: data })
    return response.data
  },

  async downloadQrcodes (link) {
    const response = await httpClient.get(`/qrcode/download/${link}`)
    return response
  }
}

const giftcards = {
  /* ############################################
  #                   giftcards                 #
  ############################################ */

  async getGiftCards (filter) {
    const response = await httpClient.get(`/giftcards`, { params: filter })
    return response
  },

  // Edit Merchant
  async requestGiftCards (data) {
    console.log('REQUESTING GIFT', data)
    const response = await httpClient.post('/giftcards', data)
    return response.data
  },

  async downloadGiftCards (link) {
    const response = await httpClient.get(`/giftcards/download/${link}`)
    return response
  },
  async validateGiftCard (data) {
    const response = await httpClient.post(`/giftcards/validate/${data}`)
    return response
  },
  // Generate vote from qrcode
  async giftcardVoteNpo (data) { // Codice npo npo_id, token token
    const response = await httpClient.post('/votes/giftcards', data)
    return response
  }
}

const appRaccoltaVoti = {
  /* #############################################
  #               APP raccolta voti              #
  ############################################# */

  async validateQrcode (data) {
    const response = await httpClient.get(`/qrcode/validate/${data.qrcodeToken}`)
    return response
  },

  async getQrcodeVoteNpos (campaign_id) {
    const response = await httpClient.get(`/npos/get_by/campaign/${campaign_id}`)
    return response
  },

  async getQrcodeGroupedNpos (campaign_id) {
    const response = await httpClient.get(`/npos/view/campaign/${campaign_id}`)
    return response
  },

  // Generate vote from qrcode
  async qrcodeVoteNpo (data) { // Codice npo npo_id, token token
    const response = await httpClient.post('/votes', data)
    return response
  },
  /* ##########################################
 #                    SURVEY
  ############################################*/

  async getSurvey (campaignId) {
    const response = await httpClient.get(`/surveys/${campaignId}`)
    return response
  },
  async createSurvey (data) { // Codice npo npo_id, token token
    const response = await httpClient.post('/surveys', data)
    return response
  },
  async updateSurvey (data) {
    const response = await httpClient.put('/surveys', data)
    return response
  },
  async addOption (data) {
    const response = await httpClient.post('/surveys/answers', data)
    return response
  },
  async deleteOption (answerId) {
    const response = await httpClient.delete('/surveys/answers/' + answerId)
    return response
  },
  async updateOption (data) {
    const response = await httpClient.put('/surveys/answers', data)
    return response
  },
  async addBanner (data) {
    const response = await httpClient.post('/surveys/banners', data)
    return response
  },
  async updateBanner (data) {
    const response = await httpClient.put('/surveys/banners', data)
    return response
  },
  async deleteBanner (bannerId) {
    const response = await httpClient.delete('/surveys/banners/' + bannerId)
    return response
  },
  async saveOption (data) { // Codice npo npo, token token
    const response = await httpClient.post('/votes', data)
    return response
  },
  async getCoupon (campaignId) {
    const response = await httpClient.get(`/surveys/${campaignId}`)
    return response
  },
  async uploadCoupon (survey_id, data) { // Codice npo npo_id, token token
    const response = await httpClient.post(`/surveys/${survey_id}/upload_coupon`, data)
    return response
  }
}

const npos = {

  /* #############################################
  #                     Npos                     #
  ############################################# */

  // Add Npo
  async newNpo (data) {
    const response = await httpClient.post('/npos', data)
    return response.data
  },

  // Edit Npo
  async updateNpo (data) {
    const response = await httpClient.put(`/npos/${data.npo_id}`, data)
    return response.data
  },

  // Delete Npo
  async deleteNpo (npoId) {
    const response = await httpClient.delete(`/npos/${npoId}`)
    return response.data
  },

  // Get specific Npo
  async getNpo (npo_id) {
    const response = await httpClient.get(`/npos/${npo_id}`)
    return response.data
  },

  // Get Npos registration statuses
  async getNpoRegistrationStatuses () {
    const response = await httpClient.get(`/npos/status/registration`)
    return response.data
  },

  // Associate Npo to campaign
  async associateNpo (data) {
    const response = await httpClient.post(`/campaigns/${data.campaign_id}/associate/npo/${data.npo_id}`, data)
    return response.data
  },

  // Associate Campaign to Npo
  async associateNpoCampaign (data) {
    const response = await httpClient.post(`/campaigns/${data.campaign_id}/associate/npo/${data.npo_id}`, data)
    return response.data
  },

  // disassociate Npo from campaign
  // Params must be campaign_id and campaign_id
  async disassociateNpo (params) {
    const response = await httpClient.delete(`/campaigns/${params.campaign_id}/dissociate/npo/${params.npo_id}`)
    return response.data
  },

  // Get Npos available for campaign association
  async getAvailableNpos (campaignId) {
    const response = await httpClient.get('/npos', { params: { campaign_id: campaignId } })
    return response.data
  },

  // Upload Npos
  // Upload Npos logo
  async uploadNpoLogo (entityType, data, entityId) {
    const response = await httpClient.post(`/${entityType}/${entityId}/upload/logo`, data)
    return response.data
  },

  async deleteLogo (entityType, entityId) {
    const response = await httpClient.delete(`/${entityType}/${entityId}/delete/logo`)
    return response.data
  },

  // Get Npos available for campaign association
  async getNpoLogo (npoId) {
    const response = await httpClient.get('/npo/logo/' + npoId)
    return response.data
  },

  // Upload Npos
  // Upload Npos CSV
  async uploadNpos (data) {
    const response = await httpClient.post('/npos/uploadfile', data)
    return response.data
  },

  // Onboarding NPO
  // #1 verify activation token on back-end
  async confirmNpo (data) {
    const response = await httpClient.post('/confirm/email', data)
    return response
  },
  // Get npo banners
  async getNpoBanners (npoId) {
    const response = await httpClient.get('/npos/' + npoId + '/banners')
    return response.data
  },
  async addNpoBanner (data) {
    const response = await httpClient.post('/npos/banners', data)
    return response
  },
  async updateNpoBanner (data) {
    const response = await httpClient.put('/npos/banners/' + data.id, data)
    return response
  },
  async deleteNpoBanner (bannerId) {
    const response = await httpClient.delete('/npos/banners/' + bannerId)
    return response
  }
}

const leads = {
  /* #########################################
  #                  Leads                   #
  ########################################## */
  async sendLeadData (data) {
    const response = await httpClient.post('/leads', data)
    return response
  },
  async confirmLeadEmail (token) {
    const response = await httpClient.get(`/votes/confirm_email/${token}`)
    return response
  },
  async exportLeads (data) {
    const response = await httpClient.get('/export/leads')
    return response.data
  }

}

const donations = {
/* #############################################
#                  Donations                   #
################################################ */
  // Get total donation per Npo per daterange
  async getTotalDonations (filter) {
    const response = await httpClient.get('/get/totaldonations', {
      params: filter
    })
    return response.data
  },

  // Get total donation per Year divided by month
  async getTotalDonationsYear (year) {
    const response = await httpClient.get('/get/totaldonations/year', {
      params: { year: year }
    })
    return response.data
  },

  // Upload donations
  // Upload donations CSV
  async uploadDonation (data) {
    const response = await httpClient.post('/upload/donations', data)
    return response.data
  },

  async getYearlyDonations (data) {
    const response = await httpClient.get('/get/donations/amount', { params: { year: data } })
    return response.data
  }
}

const lists = {
  /* #############################################
#                   Lists                      #
############################################# */

  // Get all intervention areas
  async getInterventionAreas (filter) {
    const response = await httpClient.get('/areas', {
      params: filter
    })
    return response.data
  },

  // Get all active campaigns
  async getCampaigns (filter) {
    const response = await httpClient.get('/campaigns', {
      params: filter
    })
    return response.data
  },

  // Get all active clients
  async getClientsList (filter) {
    const response = await httpClient.get('/clients', {
      params: filter
    })
    return response.data
  },

  // Get entities statuses
  async getStatuses (entity, filter) {
    const response = await httpClient.get(`/${entity}/status`, {
      params: filter
    })
    return response.data
  },

  // Get merchant clients relations
  async getMerchantRelations () {
    const response = await httpClient.get(`/merchants/relations`)
    return response.data
  }
}

const messages = {
  /* #############################################
  #                   Messages                   #
  ############################################# */
  // Get total donation per Npo per daterange
  async sendMessage (data) {
    const response = await httpClient.post('/messages', data)
    return response.data
  },

  async getMessages (entityId) {
    const response = await httpClient.get('/messages')
    return response.data
  }
}

export default {
  ...session,
  ...uploads,
  ...users,
  ...common,
  ...campaigns,
  ...clients,
  ...contacts,
  ...merchants,
  ...qrcodes,
  ...giftcards,
  ...appRaccoltaVoti,
  ...npos,
  ...leads,
  ...donations,
  ...lists,
  ...messages
}

/* #########################################
#             Request interceptors         #
########################################## */

// The interceptor will add auth token
httpClient.interceptors.request.use(function (req) {
  const token = localStorage['x-token']
  const tokenType = localStorage.token_type
  if (token != null) {
    req.headers.Authorization = `${tokenType} ${token}` // Add Token to every Call
  }
  return req
}, function (err) {
  return Promise.reject(err)
})

/* #########################################
#            Response interceptors         #
########################################## */

// Add a 401 response interceptor
httpClient.interceptors.response.use(function (response) {
  return response
}, function (error) {
  if (error.response.status === 401) {
    console.log('LOGOUT 401')
    localStorage.clear()
    store.dispatch('auth/logout')
    // VueRouter.push({ name: 'login' })
  }
  return Promise.reject(error)
})
